import { useIntl } from 'react-intl';
import Avatar from 'src/componentes/avatar';
import Button from 'src/componentes/button';
import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import Pagination from 'src/componentes/list/pagination';
import LucideIcon from 'src/componentes/lucide-icon';
import Spinner from 'src/componentes/spinner';
import StatusBadge from 'src/componentes/StatusBadge';
import useAuth from 'src/hooks/useAuth';
import { CORES_STATUS_BADGE } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';

import { getAreaNome, getCargoNome, normalizeColaborador } from './utils';
import { PARTICIPANTE_STATUS } from './constants';

const ParticipantesList = ({
  currentPage,
  disabled,
  entidadeId,
  getStatusParticipanteLabel,
  localSearchParticipantes,
  onChangePage,
  onChangePageSize,
  onClearParticipantes,
  onOpenColaborador,
  onOpenFiltro,
  onRemoveParticipante,
  onSearchChangeStart,
  pageSize,
  participantes,
  participantesFiltrados,
  participantesPaginados,
  participantesSearchLoading,
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const renderColaborador = (item) => {
    const participante = normalizeColaborador(item);

    return (
      <Avatar
        border={1}
        foto={participante?.fotoArquivo}
        label={participante?.nome}
        labelLinkCallback={(event) => onOpenColaborador(participante, event)}
        size={28}
      />
    );
  };

  const renderStatusParticipante = (item) => {
    const isPendente = item.statusSalvamento === PARTICIPANTE_STATUS.PENDENTE;
    const cores = isPendente
      ? CORES_STATUS_BADGE.laranja
      : CORES_STATUS_BADGE.verde;

    return (
      <StatusBadge
        text={getStatusParticipanteLabel(item)}
        backgroundColor={cores.corFundo}
        textColor={cores.cor}
      />
    );
  };

  const renderActions = (item) => {
    if (disabled) return null;

    const participante = normalizeColaborador(item);
    const excluirLabel = intl.formatMessage({ id: 'label.excluir' });

    return (
      <button
        type="button"
        aria-label={excluirLabel}
        title={excluirLabel}
        onClick={() => onRemoveParticipante(participante?.id)}
        className="cursor-pointer p-0 border-0 bg-transparent"
      >
        <LucideIcon icon="CircleX" size={18} />
      </button>
    );
  };

  const handleListChangeCapture = (event) => {
    if (event.target?.type === 'text') {
      onSearchChangeStart();
    }
  };

  const canClearParticipantes = !disabled && participantes.length > 0;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="font-weight-bold">
          {capitalizeFirstLetter(intl.formatMessage({ id: 'participantes' }))}:{' '}
          {participantes.length}
        </div>
        <div className="d-flex align-items-center">
          <Button
            style={{
              background: 'transparent',
              border: 0,
              marginRight: 10,
              padding: 0,
            }}
            className="text-primary"
            disabled={!canClearParticipantes}
            onClick={onClearParticipantes}
          >
            {intl.formatMessage({ id: 'limparParticipantes' })}
          </Button>
          <Button
            className="ml-2"
            color="primary"
            disabled={disabled}
            leftIcon="plus"
            iconSize={18}
            onClick={onOpenFiltro}
          >
            {intl.formatMessage({ id: 'labelParticipantes' })}
          </Button>
        </div>
      </div>
      <div
        className="position-relative"
        onChangeCapture={handleListChangeCapture}
      >
        {participantesSearchLoading && (
          <div
            style={{
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              borderRadius: 4,
              display: 'flex',
              height: 32,
              justifyContent: 'center',
              pointerEvents: 'none',
              position: 'absolute',
              right: 16,
              top: 48,
              width: 32,
              zIndex: 6,
            }}
          >
            <Spinner size={24} />
          </div>
        )}
        <List
          dataSource={participantesPaginados}
          key={`participantes-${entidadeId}`}
          fillAvailableHeight
          hideColumnsButton
          hideFiltroChip
          ignorePermissions
          local
          localSearchFilter={localSearchParticipantes}
          modelValue={{}}
          openLastFilter={false}
          pageSize={pageSize}
          showDelete={false}
          showExport={false}
          showMenu={false}
          showNew={false}
          showPage={false}
          showSearch
          showToolBar
          url="/PesquisaClimaParticipantes"
        >
          <ListColumn
            default
            headerText={capitalizeFirstLetter(resources.colaborador)}
            valueFunction={renderColaborador}
          />
          <ListColumn
            default
            headerText={capitalizeFirstLetter(resources.cargo)}
            valueFunction={getCargoNome}
          />
          <ListColumn
            default
            headerText={capitalizeFirstLetter(resources.area)}
            valueFunction={getAreaNome}
          />
          <ListColumn
            default
            headerText={capitalizeFirstLetter(
              intl.formatMessage({ id: 'status' })
            )}
            valueFunction={renderStatusParticipante}
            width={110}
          />
          <ListColumn
            className="text-right"
            default
            valueFunction={renderActions}
            width={45}
          />
        </List>
      </div>
      {participantesFiltrados.length > 0 && (
        <div className="d-flex justify-content-end mt-2">
          <Pagination
            allowPaging
            changeCurrentPage={onChangePage}
            changePageSize={onChangePageSize}
            count={participantesFiltrados.length}
            currentPage={currentPage}
            pageSize={pageSize}
          />
        </div>
      )}
    </>
  );
};

export default ParticipantesList;
